import React from 'react';
import styles from './Badge.module.css';

export type BadgeVariant =
  | 'draft'
  | 'published'
  | 'archived'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'primary';

export type BadgeStyle = 'solid' | 'outline' | 'glass';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps {
  /**
   * Color variant of the badge
   */
  variant?: BadgeVariant;

  /**
   * Style variant: solid (default), outline, or glass (with glassmorphism)
   */
  style?: BadgeStyle;

  /**
   * Size of the badge
   */
  size?: BadgeSize;

  /**
   * Show a dot indicator before the text
   */
  dot?: boolean;

  /**
   * Icon to display before the text
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon to display after the text
   */
  rightIcon?: React.ReactNode;

  /**
   * Badge content
   */
  children: React.ReactNode;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Click handler
   */
  onClick?: () => void;
}

/**
 * Badge - A flexible label component with multiple variants and styles
 *
 * Supports solid, outline, and glassmorphism styles with various color variants.
 * Can display icons, dots, and comes in multiple sizes.
 *
 * @example
 * ```tsx
 * <Badge variant="success" style="glass" dot>
 *   Active
 * </Badge>
 * ```
 */
export const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  style = 'solid',
  size = 'md',
  dot = false,
  leftIcon,
  rightIcon,
  children,
  className,
  onClick,
}) => {
  const classNames = [
    styles.badge,
    styles[variant],
    styles[`style-${style}`],
    styles[`size-${size}`],
    onClick && styles.clickable,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {dot && <span className={styles.dot} aria-hidden="true" />}
      {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}
      <span className={styles.text}>{children}</span>
      {rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={classNames}
        onClick={onClick}
        aria-label={typeof children === 'string' ? children : 'Badge'}
      >
        {content}
      </button>
    );
  }

  return <span className={classNames}>{content}</span>;
};
